/**
 * THE event taxonomy of Agentic School: the single source of truth for every
 * product analytics event, client AND server.
 *
 * - Client events are sent with `trackEvent(name, props)` (see `./index.ts`),
 *   which routes them to PostHog EU (consent-aware) and, after analytics
 *   consent, to the Portfolio Analytics tracker.
 * - Server events are sent by Convex (`convex/lib/analytics.ts`) from verified
 *   state changes only (Stripe webhook, account creation). A purchase is NEVER
 *   trusted from the browser.
 *
 * Adding an event = add its props type here AND a row to {@link EVENT_CATALOG}
 * (the compiler enforces the row; a test enforces the row is documented in
 * `docs/ANALYTICS.md`). Rules: snake_case `object_verb` names, never PII in
 * properties (no emails, names, free text a user typed beyond search terms),
 * catalog money as whole USD (`catalog_*_usd`), collected money as Stripe
 * minor units plus ISO `currency` (`*_minor`).
 *
 * This module is pure data + types: no browser or Convex imports, so the
 * Convex bundle can import it too.
 */
import type { BillingInterval, PaidPlanId, PlanId } from '../pricing'

/** Where a pricing / paywall / upsell interaction happened. */
export type ConversionSurface =
  | 'pricing_page'
  | 'home_pricing'
  | 'pro_gate'
  | 'early_access_banner'
  | 'course_callout'
  | 'account_billing'
  | 'lesson_next_step'
  | 'course_pro_path'
  | 'dashboard_upgrade'
  | 'nav_pricing'
  | 'footer_pricing'
  | 'tool_lead_capture'

/** Where a generic call-to-action button lives (for `cta_clicked`). */
export const CTA_LOCATIONS = [
  'hero',
  'final_cta',
  'nav_hero',
  'nav_sticky',
  'mobile_menu',
] as const
export type CtaLocation = (typeof CTA_LOCATIONS)[number]

/** What a generic CTA does. */
export const CTA_KINDS = [
  'signup',
  'dashboard',
  'login',
  'resources',
  'courses',
  'starter_path',
  'pricing',
] as const
export type CtaKind = (typeof CTA_KINDS)[number]

/** Events emitted in the browser. */
export type ClientAnalyticsEvents = {
  /** An interactive tool received its first input/click in this page view. */
  tool_used: { tool: string; interaction: 'input' | 'click' }
  /** A lesson page was opened (once per page view). */
  lesson_started: {
    course: string
    lesson: string
    access: 'free' | 'pro'
  }
  /** A lesson was marked complete (transition into completed only). */
  lesson_completed: { lesson: string }
  /** A lesson was saved (transition into saved only). */
  lesson_saved: { lesson: string }
  /** The onboarding flow after sign-up was finished. */
  onboarding_completed: Record<string, never>
  /** Search: a debounced query, or a result click. */
  search_used: {
    surface: 'palette' | 'search_page'
    action: 'query' | 'result_click'
    /** Normalised (trimmed, lowercased, max 80 chars) search terms. */
    query: string
    result_count: number
    result_group?: string
  }
  /** A primary call-to-action button was clicked. */
  cta_clicked: { location: CtaLocation; cta: CtaKind; href: string }
  /** Newsletter form submitted and accepted (double opt-in email sent). */
  newsletter_signup_requested: {
    source: string
    /** `active` when a signed-in, verified owner subscribed directly. */
    status: 'pending' | 'active'
  }
  /** Double opt-in link clicked: the newsletter subscription is active. */
  newsletter_confirmed: { status: 'confirmed' | 'already_confirmed' }
  /** Founding-member (waitlist) form accepted. */
  waitlist_joined: {
    surface: ConversionSurface
    plan: PaidPlanId
    interval: BillingInterval
    /** `pending` = double opt-in email sent; `joined` = verified account. */
    status: 'pending' | 'joined' | 'updated'
  }
  /** Double opt-in link clicked: the founding-member spot is confirmed. */
  waitlist_confirmed: {
    status: 'confirmed' | 'already_confirmed'
    plan: PaidPlanId | 'unknown'
  }
  /** A Pro lesson's paywall (or early-access banner) was shown. */
  paywall_viewed: {
    surface: ConversionSurface
    course: string
    lesson: string
    mode: 'locked' | 'early_access'
  }
  paywall_cta_clicked: {
    surface: ConversionSurface
    course?: string
    lesson?: string
    cta: 'unlock' | 'sign_in' | 'start_free' | 'join_founding'
  }
  /** Pricing (page or homepage section) entered the view. */
  pricing_viewed: { surface: ConversionSurface; billing_live: boolean | null }
  /** The Monthly / Yearly toggle was switched. */
  plan_toggled: { surface: ConversionSurface; interval: BillingInterval }
  plan_cta_clicked: {
    surface: ConversionSurface
    plan: PlanId
    interval: BillingInterval
    action: 'signup' | 'checkout' | 'portal' | 'waitlist' | 'start_free'
  }
  /** Checkout was requested (before the redirect to Stripe). */
  checkout_started: {
    surface: ConversionSurface
    plan: PaidPlanId
    interval: BillingInterval
    seats: number
  }
  /** An upsell surface (Pro path card, end-of-path upsell) entered the view. */
  upsell_viewed: { surface: ConversionSurface; course?: string; lesson?: string }
  /** A click on a shared conversion surface (next step, upsell, nav pricing). */
  upsell_cta_clicked: {
    surface: ConversionSurface
    cta:
      | 'next_lesson'
      | 'next_course'
      | 'pricing'
      | 'founding'
      | 'pro_course'
      | 'dashboard'
    course?: string
    lesson?: string
  }
  /**
   * Navigation timing of the document load (TTFB etc.). PostHog's web vitals
   * cover LCP, INP, CLS and FCP; TTFB is reported here.
   */
  navigation_timing: {
    ttfb_ms: number
    dom_content_loaded_ms: number
    load_ms: number | null
    transfer_kb: number | null
    navigation_type: string
  }
  /**
   * A 404 was rendered. `kind`: `route` = no such URL, `content` = a known
   * route whose lesson/course/topic does not exist. `referrer_kind` tells
   * broken internal links (fix them) from external ones (redirect them).
   */
  page_not_found: {
    /** Sanitised pathname (opaque ids collapsed to `:id`), no query. */
    path: string
    kind: 'route' | 'content'
    referrer_kind: 'internal' | 'external' | 'direct'
  }
  /** Checkout or customer-portal launch failed (lost revenue signal). */
  billing_action_failed: {
    action: 'checkout' | 'portal'
    /** Client-readable billing error code (never a raw message). */
    error_code: string
    surface?: ConversionSurface
    plan?: PaidPlanId
    interval?: BillingInterval
  }
}

/** Stripe subscription status (mirrors `convex/lib/stripeEvents.ts`). */
export type SubscriptionStatusValue =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused'

/**
 * Plan facts shared by every subscription event. Money named `catalog_*_usd`
 * is the LIST price from `src/lib/pricing.ts` (whole USD), i.e. what the plan
 * is worth on paper. It is NOT collected cash: coupons, taxes, prorations and
 * failed payments are not in it. Collected cash = `invoice_paid` minus
 * `refund_issued` (actual Stripe amounts in minor units + currency).
 *
 * `livemode`: Stripe live mode of the source object; `false` = test/sandbox
 * data, sent with `environment: 'test'` so the project's test-account filter
 * excludes it. `null` = Stripe did not say.
 */
type SubscriptionFactsProps = {
  plan: PaidPlanId
  interval: BillingInterval
  seats: number
  subscription_id: string
  livemode: boolean | null
}

/** Events emitted by Convex from verified server-side state changes. */
export type ServerAnalyticsEvents = {
  /** A Convex user row was created for a new Clerk account. */
  signup_completed: { locale: string }
  /**
   * A subscription became live for the first time (Stripe-verified). This is
   * ACTIVATION, valued at catalog prices; it is not proof of payment (see
   * `invoice_paid`).
   */
  purchase_completed: SubscriptionFactsProps & {
    /** Catalog price of the first billing period, whole USD. */
    catalog_first_period_usd: number
    /** Catalog price normalised to one month, whole USD. */
    catalog_mrr_usd: number
    currency: 'USD'
  }
  /**
   * `scheduled`: cancel at period end was requested (still paying, still
   * counted in MRR). `ended`: the subscription actually ended (immediately or
   * at the end of a scheduled cancellation): THE churn event, sent at most
   * once per subscription. Count churned MRR from `kind = ended` only.
   */
  subscription_cancelled: SubscriptionFactsProps & {
    kind: 'scheduled' | 'ended'
    /** `ended` only: whether a scheduled cancellation preceded the end. */
    after_scheduled: boolean
    catalog_mrr_usd: number
    currency: 'USD'
  }
  /** A scheduled cancellation was undone before the period ended. */
  subscription_reactivated: SubscriptionFactsProps & {
    catalog_mrr_usd: number
    currency: 'USD'
  }
  /**
   * Full subscription state after every applied change. State-derived MRR =
   * per `subscription_id` the latest event's `mrr_contribution_usd`, summed
   * (see docs/ANALYTICS.md "Revenue definitions").
   */
  subscription_state_changed: SubscriptionFactsProps & {
    status: SubscriptionStatusValue
    previous_status: SubscriptionStatusValue | null
    cancel_at_period_end: boolean
    /** Catalog MRR while the status counts as recurring (active, past_due), else 0. */
    mrr_contribution_usd: number
    catalog_mrr_usd: number
    currency: 'USD'
  }
  /** Stripe-verified collected cash for one invoice (once per invoice). */
  invoice_paid: {
    invoice_id: string
    subscription_id: string
    /** Actual amount collected, minor units (cents) of `currency`. */
    amount_paid_minor: number
    /** ISO 4217 from Stripe (upper case). */
    currency: string
    billing_reason: string | null
    /** initial = first invoice, renewal = cycle, recovery = paid after a failure, change = proration. */
    payment_kind: 'initial' | 'renewal' | 'recovery' | 'change' | 'other'
    plan: PaidPlanId | null
    interval: BillingInterval | null
    seats: number | null
    livemode: boolean | null
  }
  /** An invoice payment attempt failed (Stripe retries; access has a grace period). */
  payment_failed: {
    invoice_id: string
    subscription_id: string
    /** Amount that failed to collect, minor units. */
    amount_due_minor: number
    currency: string
    attempt_count: number | null
    billing_reason: string | null
    plan: PaidPlanId
    interval: BillingInterval
    seats: number
    catalog_mrr_usd: number
    livemode: boolean | null
  }
  /** Money returned to a customer (delta of this Stripe event). */
  refund_issued: {
    charge_id: string
    /** Newly refunded in this event, minor units. */
    refund_amount_minor: number
    /** Total refunded on the charge so far, minor units. */
    refunded_total_minor: number
    charge_amount_minor: number
    currency: string
    full_refund: boolean
    livemode: boolean | null
  }
}

export type ClientEventName = keyof ClientAnalyticsEvents
export type ServerEventName = keyof ServerAnalyticsEvents
export type AnalyticsEventName = ClientEventName | ServerEventName

/** Back-compat alias: the typed client event map. */
export type AnalyticsEvents = ClientAnalyticsEvents

export const FUNNEL_STAGES = [
  'acquisition',
  'engagement',
  'lead',
  'signup',
  'intent',
  'checkout',
  'revenue',
  'retention',
  'performance',
  'reliability',
] as const
export type FunnelStage = (typeof FUNNEL_STAGES)[number]

export type EventCatalogEntry = {
  source: 'client' | 'server'
  stage: FunnelStage
  description: string
}

/**
 * Every custom event with its source and funnel stage. `satisfies` makes a
 * missing or unknown event a compile error, so this table can never drift
 * from the prop types above. PostHog's own `$pageview`, `$pageleave`,
 * `$exception`, `$web_vitals`, `$autocapture` and `$identify` are documented
 * in `docs/ANALYTICS.md` but not listed here (we do not emit them by name).
 */
export const EVENT_CATALOG = {
  tool_used: { source: 'client', stage: 'engagement', description: 'First interaction with an interactive tool in a page view.' },
  lesson_started: { source: 'client', stage: 'engagement', description: 'A lesson page was opened.' },
  lesson_completed: { source: 'client', stage: 'retention', description: 'A lesson was marked complete.' },
  lesson_saved: { source: 'client', stage: 'retention', description: 'A lesson was saved for later.' },
  onboarding_completed: { source: 'client', stage: 'signup', description: 'Post-sign-up onboarding finished.' },
  search_used: { source: 'client', stage: 'engagement', description: 'Search query (debounced) or search result click.' },
  cta_clicked: { source: 'client', stage: 'acquisition', description: 'Hero, final CTA, nav or mobile-menu CTA clicked.' },
  newsletter_signup_requested: { source: 'client', stage: 'lead', description: 'Newsletter form accepted; double opt-in email sent.' },
  newsletter_confirmed: { source: 'client', stage: 'lead', description: 'Newsletter double opt-in confirmed.' },
  waitlist_joined: { source: 'client', stage: 'lead', description: 'Founding-member waitlist form accepted.' },
  waitlist_confirmed: { source: 'client', stage: 'lead', description: 'Founding-member double opt-in confirmed.' },
  paywall_viewed: { source: 'client', stage: 'intent', description: 'Pro paywall or early-access banner shown.' },
  paywall_cta_clicked: { source: 'client', stage: 'intent', description: 'CTA on the paywall clicked.' },
  pricing_viewed: { source: 'client', stage: 'intent', description: 'Pricing page or homepage pricing section viewed.' },
  plan_toggled: { source: 'client', stage: 'intent', description: 'Monthly / Yearly toggle switched.' },
  plan_cta_clicked: { source: 'client', stage: 'intent', description: 'A plan card CTA clicked.' },
  checkout_started: { source: 'client', stage: 'checkout', description: 'Stripe Checkout requested.' },
  upsell_viewed: { source: 'client', stage: 'intent', description: 'Upsell surface entered the viewport.' },
  upsell_cta_clicked: { source: 'client', stage: 'intent', description: 'Upsell / next-step CTA clicked.' },
  navigation_timing: { source: 'client', stage: 'performance', description: 'Document navigation timing incl. TTFB (once per page load).' },
  page_not_found: { source: 'client', stage: 'reliability', description: '404 rendered (route or missing content), with referrer kind.' },
  billing_action_failed: { source: 'client', stage: 'reliability', description: 'Checkout or portal launch failed (error code only).' },
  signup_completed: { source: 'server', stage: 'signup', description: 'Convex user created for a new Clerk account.' },
  purchase_completed: { source: 'server', stage: 'revenue', description: 'Subscription activated (Stripe-verified); catalog value, not collected cash.' },
  subscription_cancelled: { source: 'server', stage: 'revenue', description: 'Cancellation scheduled, or subscription ended (churn, once per subscription).' },
  payment_failed: { source: 'server', stage: 'revenue', description: 'Invoice payment attempt failed (Stripe-verified, actual amount due).' },
  subscription_reactivated: { source: 'server', stage: 'revenue', description: 'Scheduled cancellation undone (Stripe-verified).' },
  subscription_state_changed: { source: 'server', stage: 'revenue', description: 'Subscription state after every applied change; basis of state-derived MRR.' },
  invoice_paid: { source: 'server', stage: 'revenue', description: 'Collected cash for an invoice (actual Stripe amount, minor units).' },
  refund_issued: { source: 'server', stage: 'revenue', description: 'Refund on a charge (actual Stripe amount delta, minor units).' },
} as const satisfies Record<AnalyticsEventName, EventCatalogEntry>

export const ALL_EVENT_NAMES = Object.keys(EVENT_CATALOG) as Array<AnalyticsEventName>

/** Person properties we set (all non-PII). */
export type AnalyticsPersonProperties = {
  locale: string
  plan: PlanId
  is_pro: boolean
  entitlement_source: 'none' | 'admin' | 'subscription' | 'grant' | 'team_seat'
}

/** First-touch attribution, sent once as person `$set_once` after consent. */
export type FirstTouchProperties = {
  first_landing_page: string
  first_referrer: string
  first_referring_domain: string
  first_locale: string
  first_utm_source?: string
  first_utm_medium?: string
  first_utm_campaign?: string
  first_utm_content?: string
  first_utm_term?: string
}
